// web/src/components/ds/StageSection.tsx — DS.5 (sprint aos-ds-shell, spec §2 D3).
//
// One collapsible sidebar section (a stage, "Sin estado", or "Archivado") —
// an As Oasis PRODUCT BLOCK. The DS `Accordion` is `defaultOpen`-only
// (uncontrolled) and the collapse here is server-persisted per stage
// (`stages.store`), so the block owns the header and the body and composes
// DS pieces inside: `@ds/primitives/Icon` for the chevron, `@ds/data-display/
// Badge` (dot, secondary) for the collapsed-only unread signal. The header's
// geometry and type roles are DsShellChrome.tsx's — the composition Santi
// approved in approved-shell.png (D2) — ported verbatim, not re-derived.
//
// Moved here from Sidebar.tsx's `SectionBlock` (sprint agent-stages) with its
// behaviour intact: a component (not a render helper) because `useDroppable`
// is a hook and each section needs its own droppable registration; the
// header dot only carries signal while the rows are hidden (sprint
// stage-header-dot-collapsed-only); the relative time stays in both states;
// "Archivado" never asks for attention (`showSignals=false`). Every testid
// the tests read survives: `section-*`, `section-toggle-*`, `section-unread-*`,
// `section-time-*`, `show-more-*`, `data-drop-active`.

import type { CSSProperties, ReactNode } from 'react';
import { useDroppable } from '@dnd-kit/core';
import Badge from '@ds/data-display/Badge';
import Icon from '@ds/primitives/Icon';
import type { Agent } from '../../types';
import { formatRelativeTime } from '../../lib/time';
import { useTranslation } from '../../i18n';

export interface StageSectionProps {
  sectionKey: string;
  label: string;
  dotHex?: string;
  hollowDot?: boolean;
  agents: Agent[];
  collapsed: boolean;
  onToggleCollapsed: () => void;
  droppable: boolean;
  showWhenEmpty: boolean;
  showSignals: boolean;
  visibleLimit: number;
  showMoreLabel: (count: number) => string;
  onRevealMore: () => void;
  renderRow: (agent: Agent) => ReactNode;
}

// approved-shell.png's section header (DsShellChrome.tsx `sectionHeaderStyle`),
// on skin roles + Tailwind tokens only (`h-6` = the 24 px visible height,
// `tracking-label-wide` = the +0.04em the mockup carries — docs/ui/04-tokens.md
// §1.1). The ::after overlay in the Tailwind classes below expands the tap
// target toward 44 px without any visual change (project-context.md
// §Desktop-first keeps the ≥44 px target).
const headerStyle: CSSProperties = {
  position: 'relative',
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  gap: 'calc(var(--space-3) / 2)',
  padding: '0 var(--space-3)',
  background: 'transparent',
  border: 'none',
  cursor: 'pointer',
  fontFamily: 'inherit',
  fontSize: 'var(--type-label-md)',
  lineHeight: 'var(--type-label-md-lh)',
  fontWeight: 'var(--font-weight-medium)',
  textTransform: 'uppercase',
  textAlign: 'left',
};

// Colours are Tailwind role classes, not inline: an inline `color` cannot
// yield to the hover class. `text-muted-foreground/50` IS the skin's
// `--text-subtle` (64 % at 0.5) and `/75` its `--text-tertiary`.
const timeStyle: CSSProperties = {
  fontSize: 'var(--type-label-md)',
  textTransform: 'none',
  letterSpacing: 'normal',
};

const showMoreStyle: CSSProperties = {
  width: '100%',
  padding: 'calc(var(--space-1) * 1.5) var(--space-3)',
  background: 'transparent',
  border: 'none',
  cursor: 'pointer',
  textAlign: 'left',
  fontFamily: 'inherit',
  fontSize: 'var(--type-label-md)',
  minHeight: 'auto',
  minWidth: 'auto',
};

export function StageSection({
  sectionKey,
  label,
  dotHex,
  hollowDot,
  agents,
  collapsed,
  onToggleCollapsed,
  droppable,
  showWhenEmpty,
  showSignals,
  visibleLimit,
  showMoreLabel,
  onRevealMore,
  renderRow,
}: StageSectionProps) {
  const { t } = useTranslation();
  const { setNodeRef, isOver } = useDroppable({
    id: sectionKey,
    disabled: !droppable,
  });

  if (!showWhenEmpty && agents.length === 0) return null;

  const visible = agents.slice(0, visibleLimit);
  const hiddenCount = agents.length - visible.length;
  // Agents arrive sorted by lastActivityAt DESC from useGroupedAgents, so the
  // newest is [0]; the Sidebar's 30 s tick keeps the label fresh.
  const newest = showSignals ? agents[0] : undefined;
  const hasUnread = showSignals && collapsed && agents.some((a) => a.unread);

  return (
    <div
      ref={setNodeRef}
      className={[
        'transition-colors duration-motion-instant ease-cursor-out rounded-md py-1',
        // Drop-target highlight — subtle accent wash, no layout shift.
        isOver ? 'bg-accent/40' : '',
      ].join(' ')}
      data-testid={`section-${sectionKey}`}
      data-drop-active={isOver ? 'true' : 'false'}
    >
      <button
        type="button"
        onClick={onToggleCollapsed}
        aria-expanded={!collapsed}
        style={headerStyle}
        className="h-6 tracking-label-wide text-muted-foreground/50 hover:text-muted-foreground transition-colors duration-motion-instant after:absolute after:content-[''] after:-inset-y-2.5 after:inset-x-0"
        data-testid={`section-toggle-${sectionKey}`}
      >
        <Icon name={collapsed ? 'chevron_right' : 'expand_more'} size={12} className="shrink-0" />
        {(dotHex || hollowDot) && (
          <span
            aria-hidden="true"
            className={[
              'inline-block h-2 w-2 rounded-full shrink-0',
              // The hollow "Sin estado" dot: `border` = --border-width-default
              // (1 px), `border-muted-foreground/75` = the skin's --text-tertiary.
              hollowDot ? 'border border-muted-foreground/75' : '',
            ].join(' ')}
            style={dotHex ? { backgroundColor: dotHex } : undefined}
          />
        )}
        <span className="truncate">{label}</span>
        {/* eslint-disable-next-line i18n-enforce/no-literal-strings-in-jsx -- decorative count */}
        <span style={{ marginLeft: 'var(--space-1)', color: 'var(--text-faint)' }}>{agents.length}</span>
        {(hasUnread || newest) && (
          <span className="ml-auto flex items-center gap-1.5 shrink-0">
            {hasUnread && (
              <Badge
                variant="dot"
                color="secondary"
                standalone
                aria-label={t('agentList.unread')}
                data-testid={`section-unread-${sectionKey}`}
              />
            )}
            {newest && (
              <span
                style={timeStyle}
                className="text-muted-foreground/50"
                data-testid={`section-time-${sectionKey}`}
              >
                {formatRelativeTime(newest.lastActivityAt)}
              </span>
            )}
          </span>
        )}
      </button>
      {!collapsed && (
        <>
          {visible.map((a) => renderRow(a))}
          {hiddenCount > 0 && (
            <button
              type="button"
              onClick={onRevealMore}
              style={showMoreStyle}
              className="text-muted-foreground/75 hover:text-foreground hover:bg-accent/40 transition-colors"
              data-testid={`show-more-${sectionKey}`}
            >
              {showMoreLabel(hiddenCount)}
            </button>
          )}
        </>
      )}
    </div>
  );
}

export default StageSection;
